import { Box, Typography, Divider, Radio, CircularProgress, Select, MenuItem } from "@mui/material";
import React, { useMemo } from "react";
import { StatusItem } from "../../utils/types";
import "../../styles/analytics.css";

interface GroupByStatusItem {
  status: string;
  totalCount: number;
}

interface StatusOption {
  key: string;
  label: string;
  color: string;
}

interface StatusBreakdownCardProps {
  statusCounts: GroupByStatusItem[];
  onStatusClick?: (payload: StatusItem) => void;
  selectedStatus: StatusItem;
  loading: boolean;
  totalCount: number;
  onStatusChange?: (status: string) => void;
  statusType?: string;
  statusOptions: StatusOption[];
  filteredStatusOptions: StatusOption[];
  statusLabels: {
    statusText: string;
    breakdownText: string;
    dueText?: string;
    overdueText?: string;
  };
  showDropdown?: boolean;
  iconType?: "radio" | "square";
}


const StatusBreakdownCard = ({
  statusCounts = [],
  onStatusClick,
  selectedStatus,
  loading = false,
  totalCount,
  onStatusChange = () => {},
  statusType,
  filteredStatusOptions,
  statusLabels,
  showDropdown = true,
  iconType = "radio",
}: StatusBreakdownCardProps) => {
  const statusCountMap = useMemo(() => {
    return statusCounts.reduce<Record<string, number>>((acc, item) => {
      acc[item.status] = item.totalCount;
      return acc;
    }, {});
  }, [statusCounts]);  

  return (
    <Box position={"relative"}>
      {loading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255,255,255,0.6)"
          zIndex={2}
        >
          <CircularProgress size={32} />
        </Box>
      )}
      <Box className="analytics-status-box">
        <Box>
          <Typography className="analytics-status-text">{statusLabels.statusText}</Typography>
          {showDropdown && (
            <Typography className="status-text">{statusLabels.breakdownText}</Typography>
          )}
        </Box>
        {showDropdown ? (
          <Select
            value={statusType}
            size="small"
            className="status-card-dropdown"
            onChange={(e) => onStatusChange(e.target.value)}
            renderValue={(value) => (
              <span className="status-selected-text">
                {value === "due" ? statusLabels.dueText : statusLabels.overdueText}
              </span>
            )}
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "center",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "center",
              },
              PaperProps: {
                className: "status-dropdown-paper",
              },
              MenuListProps: {
                sx: {
                  paddingTop: 0,
                  paddingBottom: 0,
                },
              },
            }}
          >
            <MenuItem value="due" className="status-menu-item">
              <span>{statusLabels.dueText}</span>
              <span
                className={`status-dot ${statusType === "due" ? "active" : ""}`}
              />
            </MenuItem>
            <MenuItem value="overdue" className="status-menu-item">
              <span>{statusLabels.overdueText}</span>
              <span
                className={`status-dot ${statusType === "overdue" ? "active" : ""}`}
              />
            </MenuItem>
          </Select>
        ) : (
          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#000"}}>
            {statusLabels.breakdownText}
          </Typography>
        )}
      </Box>
      <Divider sx={{ color: "#E5E5E5", mt: 0.5 }} />
      {filteredStatusOptions.map((status: StatusOption) => {
        const count =
          status.key === "total"
            ? totalCount
            : (statusCountMap[status.key] ?? 0);
            const isSelected = selectedStatus?.key === status.key;
            const isTotal = status.key === "total";
            // For "total": no icon; for "not_rated": show icon but black text
            const showIcon = !isTotal;


        return (
          <React.Fragment key={status.key}>
            {status.key === "total" && <Divider sx={{ color: "#E5E5E5", mt: 1 }} />}
            <Box
              className={`analytics-status-box analytics-status-row ${
                isSelected ? "analytics-status-row--selected" : ""
              }`}
              py={0.4}
              pl={1}
              onClick={() =>
                onStatusClick?.({
                  key: status.key,
                  label: status.label,
                  count,
                })
              }
              role={onStatusClick ? "button" : undefined}
              tabIndex={onStatusClick ? 0 : undefined}
              onKeyDown={(e) => {
                if (!onStatusClick) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onStatusClick({
                    key: status.key,
                    label: status.label,
                    count,
                  });
                }
              }}
            >
              <Box className="analytics-status-option">
                {iconType === "radio" ? (
                  <Radio
                    checked={true}
                    disableRipple
                    className="status-radio"
                    sx={{
                      margin: "0px",
                      color: status.color,
                      "& .MuiSvgIcon-root": {
                        fill: status.color,
                      },
                    }}
                  />
                ) : showIcon ? (
                  <Box
                    sx={{
                      width: "14px",
                      height: "14px",
                      borderRadius: "3px",
                      backgroundColor: status.color,
                      marginRight: "4px",
                      flexShrink: 0,
                    }}
                  />
                ) : null}
                <Typography sx={{ color: "#6F6F7F", fontSize: "14px", fontWeight: "400", }}>{status.label}</Typography>
              </Box>
              <Box className="status-count-clickable">
                <Typography className="status-count">{count}</Typography>
              </Box>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default StatusBreakdownCard;
